/**
 * The wire shapes of the control-plane API.
 *
 * These mirror the hand-written views in the backend routes (`listView` in
 * routes/v1/instances.ts, `toPlanView` / `toOsTemplateView` in
 * routes/v1/catalogue.ts) rather than the database rows behind them. The backend
 * assembles every response field by field precisely so that adding a column does
 * not change what ships; declaring the same contract here keeps the panel honest
 * about what it is actually allowed to see.
 */

/**
 * The only state names a customer ever receives. The internal machine has
 * `pending`/`provisioning`, `rebuilding` and `terminating`; `customerFacingState`
 * collapses those to the three below before they cross the wire.
 */
export type InstanceState =
  | 'building'
  | 'active'
  | 'suspended'
  | 'reinstalling'
  | 'deleting'
  | 'destroyed'
  | 'failed';

export type PowerState = 'running' | 'stopped' | 'unknown';

/**
 * States the backend is still working through. Anything in this set will change
 * without the customer touching it, which is the only reason this panel ever
 * polls.
 */
export const TRANSITIONAL_STATES: readonly InstanceState[] = ['building', 'reinstalling', 'deleting'];

export function isTransitional(state: InstanceState): boolean {
  return TRANSITIONAL_STATES.includes(state);
}

/** The plan as it hangs off an instance: specs only, no price. */
export interface InstancePlan {
  id: number;
  name: string;
  slug: string;
  vcpu: number;
  ram_mb: number;
  disk_gb: number;
  bandwidth_tb: number;
}

/** Where a machine lives, or may be built. */
export interface Region {
  slug: string;
  name: string;
  /** ISO 3166-1 alpha-2, for the flag and for "which law applies" questions. */
  country: string;
}

export interface RegionOption extends Region {
  /**
   * False when the region has no free address or no node with room for the
   * plan. Offered but disabled, so a full location reads as sold out rather
   * than as us having quietly stopped selling there.
   */
  available: boolean;
}

/**
 * How far a build has got, in the four stages a customer can act on.
 *
 * The pipeline has twelve steps; these are the four that mean something to
 * somebody watching. Null whenever the machine is not being built — a progress
 * bar at zero on a stopped machine would suggest work that is not happening.
 */
export type BuildPhase = 'queued' | 'disk' | 'image' | 'booting';

export interface BuildProgress {
  phase: BuildPhase;
  /** Phases already passed, so the track can mark them without re-deriving. */
  done: BuildPhase[];
}

export interface Instance {
  id: string;
  hostname: string;
  /**
   * Name shown in the panel. Null or absent means show `hostname`.
   *
   * This is not the name inside the operating system. An API from before this
   * field existed omits it, and the panel then shows the hostname.
   */
  label?: string | null;
  /** Null for machines created before locations existed. */
  region: Region | null;
  /** Present on the detail response only, and only while building. */
  build_progress?: BuildProgress | null;
  state: InstanceState;
  power_state: PowerState;
  /** Null until the provision pipeline has allocated an address. */
  ip: string | null;
  /** Null if the plan behind this instance has since been removed. */
  plan: InstancePlan | null;
  /**
   * UTC, but a MySQL DATETIME(3) string ("2026-08-22 18:03:11.123"), not ISO
   * 8601 — the pool runs with `dateStrings: true`. Parse it with
   * `parseApiDate` from lib/format, never with a bare `new Date()`.
   */
  created_at: string;
  /** Radar hold. Pending and flagged machines cannot be driven from this panel. */
  service_hold?: 'none' | 'pending_review' | 'flagged';
}

export interface InstanceListResponse {
  instances: Instance[];
}

/** GET /api/v1/instances/:id — the list view plus what only the detail page needs. */
/**
 * This month's traffic against what the plan includes.
 *
 * OUTBOUND IS WHAT COUNTS. `bytes_in` is measured and shown and never charged:
 * inbound is what somebody else chose to send, and a customer cannot control how
 * much of it arrives. Every figure derived from the allowance therefore uses
 * `bytes_out` alone, and the server computes them so one definition of "used"
 * exists rather than two that agree until somebody edits one.
 */
export interface InstanceTraffic {
  /** 'YYYY-MM', a calendar month in UTC. */
  period: string;
  /** Free, and here because it is the number somebody wants during an attack. */
  bytes_in: number;
  bytes_out: number;
  included_bytes: number;
  /**
   * What the plan alone includes.
   *
   * Differs from `included_bytes` when an operator has given this machine a
   * larger allowance than its plan — a customer who asked for more traffic and
   * did not need a bigger CPU. Shown so the meter and the plan page they bought
   * from do not appear to disagree.
   */
  plan_included_bytes: number;
  /** Outbound over the allowance, 0..1 and beyond. */
  used_fraction: number;
  overage_bytes: number;
  overage_cost_cents: number;
  overage_per_tb_cents: number;
}

export interface InstanceDetail extends Instance {
  /**
   * The machine this VPS runs on, as a label we chose for customers.
   *
   * Null when the operator has not named the node, which is the default: the
   * panel then shows no host at all rather than the internal Proxmox name.
   */
  host: string | null;
  /**
   * Seconds since the machine booted, or null.
   *
   * Null covers three different things on purpose: not placed yet, not running,
   * and the hypervisor could not be reached. None of them is "up for zero
   * seconds", so none of them is shown as a number.
   */
  uptime_seconds?: number | null;
  os_template: { slug: string; name: string } | null;
  smtp_allowed: boolean;
  updated_at: string;
  /**
   * Absent when the API predates traffic metering.
   *
   * Optional on purpose: a deployed panel and a deployed API are not always the
   * same age, and a browser tab open across a deploy is the same skew. Typing
   * this as required is what turned a missing field into a blank page.
   */
  traffic?: InstanceTraffic;
}

export type OsFamily = 'debian' | 'ubuntu' | 'rocky' | 'alma' | 'centos' | 'other';

/*
 * There is no priced plan catalogue here on purpose. GET /api/v1/plans still
 * exists for the storefront, but nothing in this panel sells anything: the plan
 * a customer gets is decided by the entitlement they bought, and a price on this
 * side of the wall would only make an already-paid-for machine look chargeable.
 */

/** GET /api/v1/os-templates — only images proven to boot on a /32 are listed. */
export interface OsTemplate {
  id: number;
  slug: string;
  name: string;
  family: OsFamily;
  /** Activation is refused if the entitled plan's disk is smaller than this. */
  min_disk_gb: number;
}

export interface OsTemplateListResponse {
  templates: OsTemplate[];
}

/* ------------------------------------------------------------ entitlements */

/**
 * A purchase that has not been turned into a machine yet.
 *
 * Servers are not ordered in this panel any more — POST /api/v1/instances
 * answers 410. The storefront takes the money and records an entitlement; the
 * panel's whole job is turning one into a running server. There is deliberately
 * no price on this shape: it has already been paid for, and quoting a figure
 * here would only invite the question of whether it is about to be charged
 * again.
 */
export interface Entitlement {
  id: number;
  /** Which storefront sold it. Shown so the customer recognises the purchase. */
  source: 'vgncloud' | 'valuegamenode';
  /** Same MySQL DATETIME(3) shape as `Instance.created_at` — use `parseApiDate`. */
  purchased_at: string;
  /** Identical to the plan hanging off an instance: specs only, no price. */
  plan: InstancePlan;
  /**
   * Where this purchase may be activated. One entry means the choice was already
   * made — either pinned at purchase, or there is only one location. Empty means
   * a single-location deployment with no regions configured.
   */
  regions: RegionOption[];
  /** pending_review: paid, not yet approved. The setup button stays hidden. */
  hold?: 'none' | 'pending_review' | 'flagged';
}

/** GET /api/v1/entitlements — paid for and not yet activated, newest first. */
export interface EntitlementListResponse {
  entitlements: Entitlement[];
}

/**
 * POST /api/v1/entitlements/:id/activate.
 *
 * No plan: the entitlement decides that. `os_template` is resolved by slug or
 * by numeric id, so either is accepted.
 */
export interface ActivateRequest {
  os_template: string | number;
  hostname: string;
  ssh_key?: string;
  /** Region slug. Required whenever more than one is on offer. */
  region?: string;
  /** Cloudflare Turnstile token. Required when the widget is on. */
  turnstileToken?: string;
}

/** Activation answers 202 with the row, before the build starts. */
export interface ActivateResponse {
  id: string;
  state: InstanceState;
}

/* ------------------------------------------------------------------ session */

export type SsoProvider = 'vgncloud' | 'valuegamenode';

/** GET /auth/me — mirrors SessionUser in backend/src/auth/session.ts. */
export interface SessionUser {
  id: number;
  email: string;
  displayName: string | null;
  isOperator: boolean;
  provider: SsoProvider;
}

/** One sign-in button on the login screen. Named so pages can type a single one. */
export interface AuthProviderOption {
  id: SsoProvider;
  label: string;
}

/** GET /auth/providers — only providers with BOTH a client id and secret set. */
export interface ProvidersResponse {
  providers: AuthProviderOption[];
  /**
   * Public site key when Turnstile is configured. Null on a laptop with empty
   * keys; the login page then skips the widget. The secret never appears here.
   */
  turnstile: { siteKey: string } | null;
}

/** @deprecated Use ProvidersResponse. Kept so an older import keeps compiling. */
export type AuthProvidersResponse = ProvidersResponse;

/* -------------------------------------------------------------------- stats */

export type StatsRange = 'hour' | 'day' | 'week' | 'month';

export interface StatsPoint {
  /** Unix seconds. */
  t: number;
  cpu_pct: number | null;
  mem_bytes: number | null;
  mem_max_bytes: number | null;
  /** Bits per second. The backend converts Proxmox's byte figure for us. */
  net_in_bits_per_sec: number | null;
  net_out_bits_per_sec: number | null;
  /** Bytes per second, the unit disk throughput is normally read in. */
  disk_read_bytes_per_sec: number | null;
  disk_write_bytes_per_sec: number | null;
}

export interface StatsResponse {
  range: StatsRange;
  series: StatsPoint[];
}

/* ----------------------------------------------------------------- firewall */

export type FirewallDirection = 'in' | 'out';
export type FirewallAction = 'ACCEPT' | 'DROP' | 'REJECT';
export type FirewallProtocol = 'tcp' | 'udp' | 'icmp' | 'any';

export interface FirewallRule {
  id?: number;
  position?: number;
  direction: FirewallDirection;
  action: FirewallAction;
  protocol: FirewallProtocol;
  source: string | null;
  dest_port: string | null;
  comment: string | null;
  /** The API speaks 0/1 here; the editor works in booleans and converts. */
  enabled: boolean | number;
}

export interface FirewallResponse {
  rules: FirewallRule[];
}

/* ------------------------------------------------------------------ console */

export interface ConsoleTicket {
  ws_url: string;
  token: string;
  expires_at: string;
}

/**
 * One thing that happened to one of this customer's machines.
 *
 * Deliberately narrow: a title we wrote, the machine it happened to, and when.
 * The audit log behind it carries node ids, vmids and job payloads, none of
 * which crosses this boundary.
 */
export interface ActivityEvent {
  instance_id: number;
  hostname: string;
  title: string;
  /** True when support did it rather than the customer. Worth saying out loud. */
  by_support: boolean;
  /** Same MySQL DATETIME(3) shape as everywhere else — use `parseApiDate`. */
  at: string;
}

/** GET /api/v1/activity — newest first, capped at 50. */
export interface ActivityListResponse {
  activity: ActivityEvent[];
}

/* ------------------------------------------------------------------- team */

export type MemberRole = 'admin' | 'member';
/** `owner` is not a stored role; it is whoever the instance belongs to. */
export type AccessRole = 'owner' | MemberRole;

export interface TeamMember {
  user_id: number;
  email: string;
  display_name: string | null;
  role: MemberRole;
  since: string;
}

export interface TeamInvite {
  email: string;
  role: MemberRole;
  expires_at: string;
}

export interface TeamView {
  /**
   * What the reader may do here.
   *
   * Sent so the panel can offer only what will succeed. Deciding it in the
   * browser would mean two copies of the rule, and the browser's copy is the
   * one that drifts.
   */
  your_role: AccessRole;
  owner: { email: string; display_name: string | null } | null;
  members: TeamMember[];
  /** Empty for a plain member: pending invitations are not their business. */
  invites: TeamInvite[];
}

/* ----------------------------------------------------------- status notices */

export type StatusNoticeSeverity = 'info' | 'warning' | 'maintenance' | 'outage';

export type StatusNoticeDismiss = 'until_updated' | 'for_24h';

/** GET /api/v1/status-notices — only live notices that apply to this session. */
export interface StatusNotice {
  /** Numeric for local/dev table rows; `hetrix:<pageId>` when Hetrix is the source. */
  id: number | string;
  severity: StatusNoticeSeverity;
  title_en: string;
  title_fi: string;
  body_en: string | null;
  body_fi: string | null;
  created_at: string;
  updated_at: string;
  dismiss: StatusNoticeDismiss;
  audience: 'all' | { type: 'region'; slug: string };
}

export interface StatusNoticeListResponse {
  notices: StatusNotice[];
}
